import { useToastManager } from "@porsche-design-system/components-react"
import type { PurchaseRequestsView } from "@slfinrtl/ui-bff-routes"
import { putPurchaseRequestVisibilityRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useMemo, useState } from "react"
import { defineMessages, useIntl } from "react-intl"
import type { SWRInfiniteKeyedMutator } from "swr/infinite"
import { useBffDynamic } from "../useBffDynamic/useBffDynamic"

interface MutationData {
	hidden: boolean
	purchaseRequestId: string
}

const messages = defineMessages({
	hideSuccess: {
		defaultMessage: "The request is now hidden. You can find it in the 'Hidden requests' list.",
		id: "purchaseRequestList.hideButton.update.hide.success",
	},
	showSuccess: {
		defaultMessage: "The request is now visible. You can find it in the 'All requests' list.",
		id: "purchaseRequestList.hideButton.update.show.success",
	},
	visibilityUpdateError: {
		defaultMessage: "Failed to update visibility status.",
		id: "purchaseRequestList.hideButton.update.errorMessage",
	},
})

export function usePurchaseRequestVisibility(
	mutateList?: SWRInfiniteKeyedMutator<PurchaseRequestsView[]>,
	dealerEntryDn?: string,
) {
	const [isPending, setIsPending] = useState(false)

	const { addMessage } = useToastManager()
	const { formatMessage } = useIntl()
	const putVisibilitySettings = useBffDynamic(putPurchaseRequestVisibilityRoute, "inline")

	const patchPurchaseRequestVisibility = useCallback(
		(data: MutationData) => {
			setIsPending(true)
			return putVisibilitySettings({
				body: {
					dealerEntryDn: dealerEntryDn ?? "",
					hidden: data.hidden,
				},
				pathParams: {
					purchaseRequestId: data.purchaseRequestId,
				},
				queryParams: {},
			})
				.then(() => {
					addMessage({
						state: "success",
						text: formatMessage(data.hidden ? messages.hideSuccess : messages.showSuccess),
					})
					mutateList?.()
				})
				.catch(() => {
					addMessage({ state: "info", text: formatMessage(messages.visibilityUpdateError) })
				})
				.finally(() => setIsPending(false))
		},
		[addMessage, formatMessage, mutateList, putVisibilitySettings, dealerEntryDn],
	)

	return useMemo(
		() => ({
			isPending,
			patchPurchaseRequestVisibility,
		}),
		[isPending, patchPurchaseRequestVisibility],
	)
}
